import { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import api from "../api/client";
import { AuthContext } from "../auth/AuthContext";
import { usePortalData } from "../portal/context/PortalDataContext";
import { Message } from "primereact/message";
import LegalModal from "./LegalModal";
import PasswordInput from "../components/PasswordInput";

const S = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(155deg, #0d4f7a 0%, #1a6fa8 45%, #0d9488 100%)",
    padding: "20px 16px",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#4a6080",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  input: {
    width: "100%",
    border: "1.5px solid #e2eaf3",
    borderRadius: 10,
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    color: "#0f2944",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  btn: {
    width: "100%",
    padding: "13px",
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "opacity 0.15s",
  },
  logoWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 28,
    gap: 12,
  },
  logoBox: {
    width: 56,
    height: 56,
    borderRadius: 16,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  brandName: {
    fontFamily: "'Lora', Georgia, serif",
    fontSize: 22,
    fontWeight: 600,
    color: "#0d4f7a",
    letterSpacing: "-0.3px",
    margin: 0,
    textAlign: "center",
  },
  tagline: {
    fontSize: 12,
    color: "#8fa3bc",
    margin: 0,
    textAlign: "center",
  },
  heading: {
    fontSize: 17,
    fontWeight: 600,
    color: "#0f2944",
    margin: "0 0 4px",
    textAlign: "center",
  },
  subheading: {
    fontSize: 13,
    color: "#4a6080",
    margin: "0 0 28px",
    textAlign: "center",
  },
  infoBox: {
    background: "#f0f7ff",
    border: "1px solid #d0e8f8",
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 12.5,
    color: "#4a6080",
    lineHeight: 1.6,
    marginTop: 20,
    textAlign: "center",
  },
  footer: {
    textAlign: "center",
    marginTop: 22,
    fontSize: 12,
    color: "#8fa3bc",
    lineHeight: 1.6,
  },
};

function LogoSvg({ size = 28 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      {/* Head */}
      <circle cx="50" cy="24" r="10" fill="white"/>
      {/* Body */}
      <line x1="50" y1="34" x2="50" y2="68" stroke="white" strokeWidth="7" strokeLinecap="round"/>
      {/* Left arm: shoulder → elbow (out) → fist (back in and up) */}
      <polyline points="50,51 30,45 35,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right arm: mirror */}
      <polyline points="50,51 70,45 65,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Smile arc */}
      <path d="M25,70 Q50,90 75,70" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round"/>
    </svg>
  );
}


function LoginInner() {
  const navigate  = useNavigate();
  const { login, selectOrg, selectDepartment } = useContext(AuthContext);
  const { refresh } = usePortalData();
  const cardRef   = useRef(null);
  const [focused, setFocused] = useState("");  const [googleLoading, setGoogleLoading] = useState(false);
  const [error,         setError]         = useState("");
  const [googleBtnWidth, setGoogleBtnWidth] = useState(356);
  const [tab, setTab] = useState("google"); // "google" | "password"
  const [legalModal, setLegalModal] = useState(null); // "privacy" | "terms" | null
  const [form, setForm] = useState({ email: "", password: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  useEffect(() => {
    const updateWidth = () => {
      if (cardRef.current) {
        setGoogleBtnWidth(Math.min(cardRef.current.offsetWidth - 64, 356));
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setGoogleLoading(true);
    try {
      const res = await api.post("auth/google/", {
        id_token: credentialResponse.credential,
      });
      login(res.data.token, res.data.phone_verified, res.data.refresh_token);
      continueAfterLogin(res.data.phone_verified);
    } catch (err) {
      const detail = err.response?.data?.error ?? "Google sign-in failed. Please try again.";
      setError(detail);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setError("");
    if (!form.email || !form.password) {
      setError("Email and password are required"); return;
    }
    setPwLoading(true);
    try {
      const res = await api.post("auth/login/", {
        email: form.email, password: form.password,
      });
      login(res.data.token, res.data.phone_verified, res.data.refresh_token);
      continueAfterLogin(res.data.phone_verified);
    } catch (err) {
      const detail = err.response?.data?.error ?? "Invalid email or password.";
      setError(detail);
    } finally {
      setPwLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed. Please try again.");
  };

  const continueAfterLogin = async (phoneVerified) => {
    const pharmacyId = localStorage.getItem("pendingPharmacyId");
    const departmentId = localStorage.getItem("pendingDepartmentId");
    if (!pharmacyId) {
      navigate("/home");
      return;
    }

    if (!phoneVerified) {
      navigate("/verify-phone", { state: { fromRegistration: true, pharmacyId, departmentId } });
      return;
    }

    try {
      await api.post("pharmacies/link/", { pharmacy_id: pharmacyId, department_id: departmentId || undefined });
      selectOrg(pharmacyId);
      if (departmentId) selectDepartment(departmentId);
      refresh();
    } catch (err) {
      console.error("Pharmacy link failed:", err);
    } finally {
      localStorage.removeItem("pendingPharmacyId");
      localStorage.removeItem("pendingDepartmentId");
    }
    // Plain SPA navigation — selectOrg() + refresh() above already put
    // orgId and the pharmacy list in the correct state, so no reload needed.
    navigate("/home");
  };

  return (
    <div style={S.page}>
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap"
        rel="stylesheet"
      />

      <div style={S.card} ref={cardRef}>

        <div style={S.logoWrap}>
          <div style={S.logoBox}><LogoSvg size={28} /></div>
          <div>
            <p style={S.brandName}>FitMe</p>
            <p style={S.tagline}>Care for your health</p>
          </div>
        </div>

        <p style={S.heading}>Welcome back</p>
        <p style={S.subheading}>Sign in to your health portal</p>

        {error && (
          <div style={{ marginBottom: 16 }}>
            <Message severity="error" text={error} style={{ width: "100%", borderRadius: 10 }} />
          </div>
        )}

        {/* Tab switcher */}
        <div style={{
          display: "flex", borderRadius: 10,
          border: "1.5px solid #e2eaf3", overflow: "hidden", marginBottom: 22,
        }}>
          {[["google", "Google"], ["password", "Email"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              flex: 1, padding: "9px 0", border: "none", cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
              background: tab === key ? "linear-gradient(135deg, #1a6fa8, #0d9488)" : "#f8fafc",
              color: tab === key ? "white" : "#4a6080",
              transition: "all 0.15s",
            }}>{label}</button>
          ))}
        </div>

        {tab === "google" && (
          <div style={{ display: "flex", justifyContent: "center", opacity: googleLoading ? 0.7 : 1 }}>
            <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            text="continue_with"
            shape="rectangular"
            logo_alignment="left"
            width={googleBtnWidth}
            theme="outline"
          />
          </div>
        )}

        {tab === "password" && (
        <div>
          <label style={S.label}>Email</label>
          <input
            type="email"
            style={{
              ...S.input,
              borderColor: focused === "email" ? "#1a6fa8" : "#e2eaf3",
              boxShadow:   focused === "email" ? "0 0 0 3px #e8f4fd" : "none",
            }}
            value={form.email}
            onChange={set("email")}
            onFocus={() => setFocused("email")}
            onBlur={() => setFocused("")}
            placeholder="email@example.com"
            onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
          />
          <label style={{ ...S.label, marginTop: 14 }}>Password</label>
          <PasswordInput
            style={{
              ...S.input,
              borderColor: focused === "password" ? "#1a6fa8" : "#e2eaf3",
              boxShadow:   focused === "password" ? "0 0 0 3px #e8f4fd" : "none",
            }}
            value={form.password}
            onChange={set("password")}
            onFocus={() => setFocused("password")}
            onBlur={() => setFocused("")}
            placeholder="Your password"
            onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
          />
          <button
            onClick={handlePasswordLogin}
            disabled={pwLoading}
            style={{ ...S.btn, marginTop: 18, opacity: pwLoading ? 0.75 : 1 }}
          >
            {pwLoading
              ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Signing in…</>
              : <><i className="pi pi-sign-in" style={{ fontSize: 14 }} /> Sign In</>
            }
          </button>
          <div style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#8fa3bc" }}>
            No account?{" "}
            <span onClick={() => navigate("/register")}
              style={{ color: "#1a6fa8", fontWeight: 600, cursor: "pointer" }}>
              Register here
            </span>
          </div>
        </div>
      )}

        <div style={S.infoBox}>
          <i className="pi pi-info-circle" style={{ marginRight: 6, color: "#1a6fa8" }} />
          After signing in, you'll be asked to verify your mobile number to connect with your pharmacy records.
        </div>

        <div style={S.footer}>
          By continuing you agree to our{" "}
          <span onClick={() => setLegalModal("terms")} style={{ color: "#1a6fa8", cursor: "pointer", textDecoration: "underline" }}>Terms of Service</span>
          {" "}and{" "}
          <span onClick={() => setLegalModal("privacy")} style={{ color: "#1a6fa8", cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</span>
        </div>

        {/* Legal modal */}
        <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />

      </div>

      <div style={{ marginTop: 24, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
        Secure customer portal · Powered by Coffer Smart
      </div>
    </div>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginInner />
    </GoogleOAuthProvider>
  );
}